// =============================================================================
// ui/tabs/AccountSetupModal.tsx — "Set up account", the + New Account flow.
// -----------------------------------------------------------------------------
// Transcribed from Josh's two screenshots (the modal scrolls, so it took two).
// Eight blocks: type · shape · firm · identity · activated-from · fee · daily
// loss limit · a preview of what the shape prefills.
//
// ── 🔴 IT IS ONE MODAL, NOT A SIX-STEP WIZARD ──
//
// The rebuild notes said otherwise and were wrong. The six-step path is dead
// code in the old client and its own source says so — `mode === 'new-account'`
// is unreachable, and the account redesign pointed both "+ New Account" buttons
// at this single modal. The screenshots settled it.
//
// ── ⚠ THE SHAPES ARE NOT FIRM RULES, AND THE COPY MUST KEEP SAYING SO ──
//
// The old client REMOVED per-firm rule templates because a stale copy feeds
// `computeDrawdownFloor` and hands the trader a wrong drawdown buffer. What is
// offered instead is four generic sizes and the trader's OWN saved templates,
// each stamped with its age. Do not add a firm name to a built-in shape.
// =============================================================================

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ACCOUNT_SHAPES, accountShapeById, buildAccountFromSetup, findExternalIdOwner,
  parsePropTemplates, shapeRulesFor, templateAge,
  type AccountSource, type FeeCadence, type PropTemplate, type SetupType,
} from '../../selectors/accountSetup';
import { createAccount } from '../../io/sync/createAccount';
import { deletePropTemplate } from '../../io/sync/propTemplates';
import { useSupabase } from '../state/supabase';
import { useAccounts } from '../state/accounts';
import { useAccountCap } from '../state/useFreeTierCap';
import { FreeTierLimit } from '../shell/FreeTierLimit';
import { usePreferences } from '../state/preferences';
import { useRange } from '../state/range';
import { moneyTarget } from '../format';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface AccountSetupModalProps {
  onCancel: () => void;
  /** Fired after a successful create, so the tab can reload. */
  onCreated: (accountId: string) => void;
}

const DEFAULT_SHAPE = 'gen-50k-eod';

export function AccountSetupModal({ onCancel, onCreated }: AccountSetupModalProps) {
  const { sb, userId } = useSupabase();
  const { accounts } = useAccounts();
  // 🚨 See the refusal branch below the state block — #65.
  const { blocked: capped } = useAccountCap();
  const { prefs, reload: reloadPrefs } = usePreferences();
  const { today } = useRange();

  const [type, setType] = useState<SetupType>('eval');
  const [shapeId, setShapeId] = useState(DEFAULT_SHAPE);
  const [firm, setFirm] = useState('');
  const [rulesUrl, setRulesUrl] = useState('');
  const [name, setName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [activatedFrom, setActivatedFrom] = useState('none');
  const [feeAmount, setFeeAmount] = useState('');
  /**
   * ⚠ HELD IN STATE, NOT READ OFF THE DOM, and null means "not chosen yet".
   * The default differs by type — funded accounts are billed monthly far less
   * often than evals — but an explicit choice must survive flipping the type,
   * which the old client got wrong once and fixed.
   */
  const [cadence, setCadence] = useState<FeeCadence | null>(null);
  const [dailyLossLimit, setDailyLossLimit] = useState('');
  /**
   * 🚨 ONLY SHOWN WHEN THE CHOSEN SHAPE SUPPLIES NO SIZE, which today means
   * `custom`. Every built-in shape IS a size, so asking twice would invite the
   * two to disagree.
   *
   * ▶ It exists because without it `custom` could not be completed at all
   * (tracker #32, from a real user): the account was born at size 0, the card
   * demanded "account size" for ever, and the "Finish setup" link on that card
   * opened this very modal — which had no field that could supply one.
   */
  const [accountSize, setAccountSize] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The id of an account that WAS created but whose fee row was rejected.
   * Non-null puts the modal into its acknowledge state — see `submit`.
   */
  const [created, setCreated] = useState<string | null>(null);

  /** The id currently being deleted, so only that row's × goes dead. */
  const [deletingTpl, setDeletingTpl] = useState<string | null>(null);
  const [tplError, setTplError] = useState<string | null>(null);

  const templates = useMemo(
    () => parsePropTemplates(prefs?.propTemplates ?? []), [prefs],
  );

  /**
   * Delete a saved template from inside the picker.
   *
   * ⚠ THE SELECTION HAS TO MOVE WITH IT. Deleting the row that is currently
   * chosen would otherwise leave `shapeId` pointing at nothing: `accountShapeById`
   * returns null, the rules preview empties, and Create would build an account
   * with no rules at all from a form that still looks filled in.
   */
  async function removeTemplate(id: string) {
    if (!sb || !userId) return;
    setDeletingTpl(id);
    setTplError(null);
    try {
      const res = await deletePropTemplate(sb, userId, id);
      if (!res.ok) { setTplError(res.error ?? 'That template was not deleted.'); return; }
      if (shapeId === id) setShapeId(DEFAULT_SHAPE);
      reloadPrefs();
    } catch (e) {
      setTplError(`That template was not deleted: ${reportFailure('delete account template', e)}`);
    } finally {
      setDeletingTpl(null);
    }
  }
  const template = useMemo<PropTemplate | null>(
    () => templates.find((t) => t.id === shapeId) ?? null, [templates, shapeId],
  );
  const shape = accountShapeById(shapeId);
  const rules = template ? template.rules : shapeRulesFor(shapeId);

  const isFunded = type === 'funded';
  const cadenceNow: FeeCadence = cadence ?? (isFunded ? 'once' : 'monthly');

  /**
   * ⚠ EVALS ONLY, and excluding nothing else. The list answers "which eval did
   * this funded account come from", so a funded account cannot be its own
   * parent and a personal account was never an attempt.
   */
  const evalAccounts = useMemo(
    () => (accounts ?? []).filter(
      (a) => a.accountType === 'combine' || a.accountType === 'sim-funded',
    ),
    [accounts],
  );

  const dupe = useMemo(
    () => findExternalIdOwner(externalId, (accounts ?? []).map((a) => ({
      id: String(a.id), name: a.name, externalId: a.externalId,
    }))),
    [externalId, accounts],
  );

  async function submit() {
    if (!sb || !userId || !today) return;
    setBusy(true);
    setError(null);

    const built = buildAccountFromSetup({
      accountType: type, shapeId,
      name, firm, externalId, accountSize,
      feeAmount, feeCadence: cadenceNow,
      activatedFrom, dailyLossLimit,
      today,
    });

    if (built.errors.includes('name')) {
      setBusy(false);
      setError('Give the account a name.');
      return;
    }


    // ⚠ The external id is what auto-routes imported trades, so a second account
    // claiming one silently splits a book in two. The old client does not treat
    // this as an error — it says it will use the existing account instead, which
    // is the right answer for a user who forgot they already added it.
    if (dupe) {
      setBusy(false);
      setError(
        `An account with ID “${externalId.trim()}” already exists (“${dupe.name}”). `
        + 'Trades from your logger already route there — edit that account instead '
        + 'of creating a second one.',
      );
      return;
    }

    // ⚠ A JSONB BLOCK, and `verifiedDate` stays NULL. It records when a HUMAN
    // last checked these numbers against the firm's page; nobody has.
    const source: AccountSource | null = rulesUrl.trim()
      ? { url: rulesUrl.trim(), name: firm.trim() || null, verifiedDate: null }
      : null;

    // ⚠ A TEMPLATE'S rules override the shape's — `buildAccountFromSetup` only
    // knows the built-in catalogue, and a template is the whole reason the user
    // picked that row.
    const withTemplate = template
      ? {
        ...built.account,
        rules: template.rules,
        accountSize: template.accountSize ?? built.account.accountSize,
        startingBalance: template.accountSize ?? built.account.startingBalance,
        currentBalance: template.accountSize ?? built.account.currentBalance,
      }
      : built.account;

    // 🚨 REFUSED HERE RATHER THAN CREATED BROKEN, and AFTER the template has had
    // its say — a template carries its own size, so checking `built` alone would
    // reject a perfectly good template-based account. A size of 0 is not a
    // cosmetic gap: it is `startingBalance` and `currentBalance` too, so every
    // drawdown gauge, the loss-room bar and Risk of Ruin measure against
    // nothing, and the account looks entirely ordinary while they do.
    // Tracker #32.
    if (!(Number(withTemplate.accountSize) > 0)) {
      setBusy(false);
      setError('Give the account a size — the drawdown and loss-room figures are all measured against it.');
      return;
    }

    const res = await createAccount(sb, userId, {
      ...withTemplate, source, adjustments: built.adjustments,
    });
    setBusy(false);

    if (!res.ok) { setError(res.error ?? 'The account could not be created.'); return; }

    // 🚨 THE WARNING MUST OUTLIVE THIS COMPONENT'S DECISION TO CLOSE.
    //
    // The first version did `setError(res.feeWarning); onCreated(...)` — and
    // `onCreated` unmounts this modal, so the state was set on a component that
    // disappeared in the same commit. Josh created "Lucid Pro 22" with a $70 fee,
    // the fee row was rejected, and he saw NOTHING. The exact silent outcome the
    // whole two-table design was written to prevent, shipped by the code that
    // argues against it.
    //
    // ⚠ THE TEST PASSED THROUGHOUT, because its `onCreated` was a `vi.fn()` that
    // does not unmount anything — so it proved the warning renders in a situation
    // that never occurs. A fixture that cannot reproduce the caller's behaviour
    // cannot test the caller's behaviour.
    //
    // So a warning STOPS here: the account exists, the modal stays, and the only
    // way out is a button that acknowledges it.
    if (res.feeWarning) {
      setCreated(res.accountId);
      setError(res.feeWarning);
      return;
    }
    onCreated(res.accountId!);
  }

  // ── 🚨 THE FREE-TIER ACCOUNT CAP — #65 ──
  //
  // This modal is the ONLY caller of `createAccount`, and `createAccount` holds
  // the only insert into `accounts` in the whole client. Both "+ New Account"
  // buttons render this component, so refusing here covers every path there is.
  //
  // ⚠ INERT while `PAID_LAUNCH` is false — `blocked` is null for everyone.
  // ⚠ Archived accounts still count. Archiving is de-clutter, not deletion.
  if (capped) {
    return createPortal(
      <FreeTierLimit title="New account" message={capped} onClose={onCancel} />,
      document.body,
    );
  }

  return createPortal(
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Set up account">
      <div className="modal wide">
        <header className="modal-head">
          <b>Set up account</b>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>×</button>
        </header>

        <div className="modal-body acs-body">
          <Block label="Account type" hint="Drives the fee shape below, and lets the Economics report work out what each funded account actually cost to reach.">
            <div className="acs-seg" role="group" aria-label="Account type">
              <button type="button" className={type === 'eval' ? 'on' : ''}
                onClick={() => setType('eval')}>Eval</button>
              <button type="button" className={isFunded ? 'on' : ''}
                onClick={() => setType('funded')}>Funded</button>
            </div>
          </Block>

          <Block
            label="Account shape"
            hint={`Starting shapes, not firm rules — everything stays editable afterwards.${
              templates.length
                ? ' Your own templates carry the numbers you saved; check them against your firm if it has been a while.'
                : ''}`}
          >
            <div className="acs-shapes">
              {ACCOUNT_SHAPES.map((s) => (
                <button
                  type="button" key={s.id}
                  className={`acs-shape${s.id === shapeId ? ' on' : ''}`}
                  aria-pressed={s.id === shapeId}
                  onClick={() => setShapeId(s.id)}
                >
                  <span className="acs-sz">{s.label}</span>
                  <span className="acs-mech">{s.summary}</span>
                  <span className="acs-tick" aria-hidden="true">✓</span>
                </button>
              ))}
            </div>

            {templates.length ? (
              <>
                <div className="acs-tpl-h">Your templates</div>
                <div className="acs-shapes">
                  {/* ⚠ A DIV WITH `role="button"`, NOT A `<button>`, and only
                      because the delete control has to live INSIDE the row —
                      nesting a button in a button is invalid and browsers drop
                      one of them. The keyboard handler is what keeps it usable;
                      the built-in shapes above are real buttons and stay so. */}
                  {templates.map((t) => (
                    <div
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={t.id === shapeId}
                      className={`acs-shape acs-tpl${t.id === shapeId ? ' on' : ''}`}
                      onClick={() => setShapeId(t.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShapeId(t.id);
                        }
                      }}
                    >
                      <span className="acs-sz">{t.label}</span>
                      <span className="acs-mech">
                        {t.summary}{t.firm ? ` · ${t.firm}` : ''}
                      </span>
                      {/* 🚨 THE AGE IS NOT DECORATION. Per-firm templates were
                          removed because stale rules feed the drawdown floor;
                          showing how old a saved one is, is the honest version
                          of offering them at all. */}
                      <span className="acs-tpl-age">
                        {templateAge(t.savedAt, `${today}T12:00:00.000Z`)}
                      </span>
                      {/* ⚠ NO CONFIRMATION, and that is the right weight. A
                          template is copied into an account at creation time and
                          never read again, so deleting one cannot move a single
                          gauge on any account already made from it. */}
                      <button
                        type="button"
                        className="acs-tpl-del"
                        aria-label={`Delete template ${t.label}`}
                        title="Delete this template"
                        disabled={deletingTpl === t.id}
                        onClick={(e) => { e.stopPropagation(); void removeTemplate(t.id); }}
                      >×</button>
                      <span className="acs-tick" aria-hidden="true">✓</span>
                    </div>
                  ))}
                </div>
                {tplError ? <p className="red acs-error">{tplError}</p> : null}
              </>
            ) : null}
          </Block>

          {/* 🚨 ONLY FOR A SHAPE THAT CARRIES NO SIZE — `custom`. Every built-in
              shape IS a size and a template saves the one it was captured from,
              so showing this alongside either would invite two answers to the
              same question. Without it `custom` could not be completed at all:
              the account was created at 0 and the card's own "Finish setup"
              link led back here, where nothing could fix it. Tracker #32. */}
          {!template && shape && shape.accountSize == null ? (
            <Block
              label="Account size"
              hint="What the firm calls the plan — 50000 for a 50k. Every drawdown gauge, the loss-room bar and Risk of Ruin are measured against it, so an account without one shows nothing useful."
            >
              <input inputMode="decimal" value={accountSize}
                onChange={(e) => setAccountSize(e.target.value)}
                placeholder="e.g. 50000" />
            </Block>
          ) : null}

          <Block label="Firm" hint="Used for grouping and reporting — not for rules. We link out instead of copying, because firms change rules often and a stale copy would quietly hand you wrong drawdown math.">
            <input value={firm} onChange={(e) => setFirm(e.target.value)}
              placeholder="Topstep / Apex / Personal" />
            <input value={rulesUrl} onChange={(e) => setRulesUrl(e.target.value)}
              placeholder="Link to the firm’s rules page (optional)" />
          </Block>

          <Block label="Identity" hint="The ID is what auto-routes trades from your logger.">
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Nickname — e.g. Topstep 50k #2" />
            <input value={externalId} onChange={(e) => setExternalId(e.target.value)}
              placeholder="Account ID — must match your ATAS / Sierra exports exactly" />
            {/* ⚠ Warned about WHILE TYPING, not only on save. Finding out after
                filling in eight fields that this account already exists is the
                worst moment to be told. */}
            {dupe ? (
              <span className="acs-dupe">
                <b>{dupe.name}</b> already uses this ID — your logger’s trades already
                route there.
              </span>
            ) : null}
          </Block>

          {/* ⚠ FUNDED ONLY, as in the old client. An eval has nothing to have
              come from, and the question on one would be nonsense. */}
          {isFunded ? (
            <Block label="Activated from" hint="Links this to the eval you paid for, so cost-per-funded counts every attempt it took. Plenty of funded accounts came from an eval that predates this app — say so and it stops being asked for.">
              <select value={activatedFrom} onChange={(e) => setActivatedFrom(e.target.value)}>
                {/* 🚨 A REAL ANSWER, not a placeholder. Without a way to say
                    "no eval tracked here", the completion nudge demands a link
                    that cannot exist and never clears. */}
                <option value="none">Not from an eval tracked here</option>
                {evalAccounts.map((a) => (
                  <option key={a.id} value={String(a.id)}>{a.name || 'Untitled'}</option>
                ))}
              </select>
            </Block>
          ) : null}

          <Block
            label="Account fee"
            hint={`Real money out. This is the only place the Economics report gets its expense side — skip it and the ROI is fiction. A monthly fee stops accruing when you archive the account. Free is for a comped or giveaway account: it still counts as an attempt in the pipeline, contributes $0 to spend, and stops this being flagged as unfinished.${
              isFunded ? ' On a funded account this is logged as the activation fee.' : ''}`}
          >
            <div className="acs-row2">
              <input inputMode="decimal" value={feeAmount}
                onChange={(e) => setFeeAmount(e.target.value)} placeholder="e.g. 165" />
              <div className="acs-seg" role="group" aria-label="Fee cadence">
                {(['once', 'monthly', 'free'] as const).map((c) => (
                  <button type="button" key={c} className={cadenceNow === c ? 'on' : ''}
                    onClick={() => setCadence(c)}>
                    {c === 'once' ? 'One-time' : c === 'monthly' ? 'Monthly' : 'Free'}
                  </button>
                ))}
              </div>
            </div>
          </Block>

          <Block
            label="Daily loss limit"
            hint={`Drives the card’s Today’s Loss Room bar — without it that bar reads “Not configured”. Leave blank to keep the shape’s default${
              rules ? ` (${moneyTarget(rules.dailyLossLimit)})` : ''}; firms vary, so check yours.`}
          >
            <input inputMode="decimal" value={dailyLossLimit}
              onChange={(e) => setDailyLossLimit(e.target.value)}
              // ⚠ The SELECTED shape's figure, not a static hint — it changes
              // with the picker above, which is what makes "from the shape"
              // true rather than decorative.
              placeholder={rules ? `${moneyTarget(rules.dailyLossLimit).replace('$', '')} from the shape` : 'e.g. 1000'} />
          </Block>

          <Block label="Rules" hint={rules ? 'Check these against your firm’s page before trusting the gauges.' : ''}>
            {rules ? (
              <details className="acs-pre">
                <summary>
                  Prefilled from {template?.label ?? shape?.label} · {template?.summary ?? shape?.summary} — edit after saving
                </summary>
                <div className="acs-pregrid">
                  <span>Max drawdown</span><span>{moneyTarget(rules.maxDDValue)}</span>
                  <span>Drawdown type</span><span>{rules.maxDDType ?? '—'}</span>
                  <span>Daily loss limit</span><span>{moneyTarget(rules.dailyLossLimit)}</span>
                  <span>Profit target</span><span>{moneyTarget(rules.profitTarget)}</span>
                  <span>Consistency</span><span>{rules.consistencyRulePct}%</span>
                </div>
              </details>
            ) : (
              <span className="acs-hint">
                Custom — nothing prefilled. Set the drawdown and targets in the
                account’s Edit form once it exists.
              </span>
            )}
          </Block>

          {error ? <p className="acs-error red">{error}</p> : null}
        </div>

        {/* 🚨 THE ACKNOWLEDGE FOOTER. When the account saved and its fee did not,
            there is nothing left to create — offering "Create account" again
            would make a second account, and offering "Skip for now" would let
            the warning be dismissed as if it were chrome. One button, and it
            says what it is agreeing to. */}
        {created ? (
          <footer className="modal-foot">
            <div className="grow" />
            <button type="button" className="btn primary"
              onClick={() => onCreated(created)}>
              I’ll add the fee myself
            </button>
          </footer>
        ) : (
          <footer className="modal-foot">
            {/* ⚠ "Skip for now", not "Cancel" — the old client's word, and it is
                the right one: this modal also opens from a first-run path where
                cancelling is a legitimate choice rather than an abort. */}
            <button type="button" className="btn" onClick={onCancel} disabled={busy}>
              Skip for now
            </button>
            <div className="grow" />
            <button type="button" className="btn primary" disabled={busy || !name.trim()}
              onClick={() => void submit()}>
              {busy ? 'Creating…' : 'Create account'}
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

function Block(
  { label, hint, children }:
  { label: string; hint?: string; children: React.ReactNode },
) {
  return (
    <div className="acs-fld">
      <div className="acs-lbl">{label}</div>
      {children}
      {hint ? <span className="acs-hint">{hint}</span> : null}
    </div>
  );
}
